// client-error-capture.js
// Robust client-side error and log capture for production apps (Vue-compatible)
// Drop-in replacement for your current snippet.

(function () {
  'use strict';

  // --- 0) Config ------------------------------------------------------------
  const CONFIG = {
    endpoint: '/api/log_error/', // your backend endpoint
    maxLogBuffer: 200,           // keep last N console lines
    maxFrames: 15,               // stack frames to send
    throttleMs: 5 * 60 * 1000,   // dedupe window per unique error key
    maxKeyLen: 500,              // dedupe key length cap
    // DISABLED: No source map resolution to avoid 404 requests
    enableSourceMapResolution: false,
    captureWarnings: false,
    
    ignorePatterns: [
      'maps.googleapis.com',
      'maps.gstatic.com',
      'google.com/maps',
      '@googlemaps/js-api-loader',
      'googletagmanager',
      'gtag.js',
            
      'chrome-extension://',
      'moz-extension://',
      'safari-extension://',
      'safari-web-extension://',
      'extensions/',
      
      'sentry.io',
      'sentry-cdn.com',
      'logrocket.com',
      'logrocket.io',
      'rollbar.com',
      'rollbarcdn.com',

      'adblock',
      'uBlock',
      'ghostery',
    ],
    
    appFilePatterns: [
      '/src/',
      '/base/',
      '/customizations/',
      '/model/',
      '/services/',
      'booking.js',
      'main.app.js',
      '.vue',
    ],
  };

  // --- 1) Utilities ---------------------------------------------------------
  
  function shouldIgnoreError(error, evt) {
    try {
      const message = error?.message || String(error);
      const stack = error?.stack || '';
      
      for (const pattern of CONFIG.ignorePatterns) {
        if (message.toLowerCase().includes(pattern.toLowerCase()) ||
            stack.toLowerCase().includes(pattern.toLowerCase())) {
          console.debug(`[error-reporter] Ignorando error de terceros: ${pattern}`);
          return true;
        }
      }
      
      if (evt && evt.filename) {
        const filename = evt.filename.toLowerCase();
        for (const pattern of CONFIG.ignorePatterns) {
          if (filename.includes(pattern.toLowerCase())) {
            console.debug(`[error-reporter] Ignorando error de archivo externo: ${evt.filename}`);
            return true;
          }
        }
      }
      
      if (stack) {
        const frames = parseStackFrames(stack);
        const hasAppFrame = frames.some(frame => {
          if (!frame.file) return false;
          const file = frame.file.toLowerCase();
          return CONFIG.appFilePatterns.some(pattern => 
            file.includes(pattern.toLowerCase())
          );
        });
        
        if (frames.length > 0 && !hasAppFrame) {
          console.debug(`[error-reporter] Ignorando error sin frames de la aplicación`);
          return true;
        }
      }
      
      if (evt && evt.target) {
        const src = evt.target.src || evt.target.href || '';
        if (src) {
          for (const pattern of CONFIG.ignorePatterns) {
            if (src.toLowerCase().includes(pattern.toLowerCase())) {
              console.debug(`[error-reporter] Ignorando error de recurso externo: ${src}`);
              return true;
            }
          }
        }
      }
      
      return false;
    } catch (e) {
      console.warn('[error-reporter] Error al verificar si ignorar:', e);
      return false;
    }
  }
  
  function safeSerialize(value) {
    const seen = new WeakSet();
    return JSON.stringify(value, function replacer(key, val) {
      if (val instanceof Error) {
        return { name: val.name, message: val.message, stack: val.stack };
      }
      if (typeof val === 'object' && val !== null) {
        if (seen.has(val)) return '[Circular]';
        seen.add(val);

        // Handle Vue component instances
        if (val.$options && val._isVue) {
          return '[Vue Component Instance]';
        }
      }
      if (typeof val === 'function') return `[Function ${val.name || 'anonymous'}]`;
      return val;
    });
  }

  function normalizeError(err) {
    if (err instanceof Error) {
      return {
        name: err.name || 'Error',
        message: err.message || String(err),
        stack: err.stack ? String(err.stack) : null,
        cause: err.cause ? String(err.cause) : null,
      };
    }
    try {
      return { name: 'NonError', message: String(err), stack: null, cause: null };
    } catch {
      return { name: 'NonError', message: '[Unstringifiable]', stack: null, cause: null };
    }
  }

  function cleanWebpackPath(filePath) {
    if (!filePath) return null;

    // Clean webpack-internal paths to show original source paths
    if (filePath.startsWith('webpack-internal:///')) {
      const cleaned = filePath.replace('webpack-internal:///', '');

      // Handle relative paths like ./src/...
      if (cleaned.startsWith('./')) {
        return cleaned;
      }

      // Handle absolute webpack paths like ../customizations/...
      if (cleaned.startsWith('../')) {
        return cleaned;
      }

      // Default case
      return cleaned;
    }

    return filePath;
  }

  // --- Source Map Resolution ------------------------------------------------
  async function fetchSourceMap(jsUrl) {
    try {
      const mapUrl = jsUrl + '.map';
      const response = await fetch(mapUrl);
      if (!response.ok) return null;
      return await response.json();
    } catch {
      return null;
    }
  }

  function resolveSourceMapPosition(sourceMap, line, column) {
    if (!sourceMap || !sourceMap.mappings) return null;

    try {
      // Simple source map resolution - for production use a proper library like 'source-map'
      // This is a basic implementation to demonstrate the concept
      const sources = sourceMap.sources || [];
      const sourcesContent = sourceMap.sourcesContent || [];

      // Find the most likely source file based on the error location
      for (let i = 0; i < sources.length; i++) {
        const source = sources[i];
        if (source && (source.includes('booking.js') || source.includes('model'))) {
          return {
            source: source,
            line: line, // In a real implementation, this would be mapped
            column: column,
            content: sourcesContent[i] || null
          };
        }
      }

      return {
        source: sources[0] || null,
        line: line,
        column: column,
        content: sourcesContent[0] || null
      };
    } catch {
      return null;
    }
  }

  async function enhanceFrameWithSourceMap(frame) {
    if (!CONFIG.enableSourceMapResolution || !frame.isSourceMapped || !frame.originalFile) {
      return frame;
    }

    // En producción, priorizar mapeos conocidos
    if (PRODUCTION_CONFIG.useKnownMappingsOnly) {
      // Si ya tiene mapeo conocido, usarlo
      if (frame.hasCorrectedMapping) {
        return frame;
      }

      // Si no tiene mapeo conocido y está habilitado el fallback, intentar source map privado
      if (PRODUCTION_CONFIG.fallbackToPrivateSourceMaps) {
        try {
          return await tryPrivateSourceMap(frame);
        } catch {
          // Si falla, devolver frame original
          return frame;
        }
      }

      return frame;
    }

    try {
      // Extract the base JS file URL from webpack-internal path
      const originalFile = frame.originalFile;
      let jsUrl = null;

      if (originalFile.includes('webpack-internal:///')) {
        // Try to map to actual served JS file
        const cleaned = cleanWebpackPath(originalFile);
        if (cleaned.startsWith('./src/base/')) {
          jsUrl = '/base/main.app.js'; // Adjust based on your build output
        }
      }

      if (!jsUrl) return frame;

      const sourceMap = await fetchSourceMap(jsUrl);
      if (!sourceMap) return frame;

      const resolved = resolveSourceMapPosition(sourceMap, frame.line, frame.column);
      if (resolved) {
        return {
          ...frame,
          resolvedSource: resolved.source,
          resolvedLine: resolved.line,
          resolvedColumn: resolved.column,
          hasResolvedSourceMap: true,
          // Try to extract method name from source content
          extractedMethod: extractMethodFromContent(resolved.content, resolved.line)
        };
      }
    } catch (e) {
      console.warn('[source-map] Failed to resolve source map for frame:', e);
    }

    return frame;
  }

  async function tryPrivateSourceMap(frame) {
    try {
      // Solo intentar si el usuario tiene permisos (ej: cookie de desarrollador)
      if (!hasSourceMapAccess()) {
        return frame;
      }

      const originalFile = frame.originalFile;
      if (!originalFile.includes('webpack-internal:///')) {
        return frame;
      }

      // Construir URL del source map privado
      const cleaned = cleanWebpackPath(originalFile);
      const mapUrl = `${PRODUCTION_CONFIG.privateSourceMapUrl}${cleaned.replace(/\//g, '_')}.map`;

      const sourceMap = await fetchSourceMap(mapUrl);
      if (!sourceMap) {
        return frame;
      }

      const resolved = resolveSourceMapPosition(sourceMap, frame.line, frame.column);
      if (resolved) {
        return {
          ...frame,
          resolvedSource: resolved.source,
          resolvedLine: resolved.line,
          resolvedColumn: resolved.column,
          hasResolvedSourceMap: true,
          extractedMethod: extractMethodFromContent(resolved.content, resolved.line),
          sourceMapType: 'private'
        };
      }
    } catch (e) {
      console.warn('[private-source-map] Failed to resolve:', e);
    }

    return frame;
  }

  function hasSourceMapAccess() {
    // Verificar si el usuario tiene acceso a source maps
    // Ej: cookie de desarrollador, header especial, etc.
    try {
      return document.cookie.includes('dev_access=true') ||
        localStorage.getItem('developer_mode') === 'true' ||
        window.location.hostname === 'localhost';
    } catch {
      return false;
    }
  }

  function extractMethodFromContent(content, line) {
    if (!content || !line) return null;

    try {
      const lines = content.split('\n');

      // Look for getter/setter/method patterns around the target line
      for (let i = Math.max(0, line - 10); i < Math.min(lines.length, line + 5); i++) {
        const currentLine = lines[i];

        // Look for getter pattern: get methodName() {
        const getterMatch = currentLine.match(/get\s+(\w+)\s*\(\s*\)\s*\{/);
        if (getterMatch && i <= line) {
          return `get ${getterMatch[1]}()`;
        }

        // Look for setter pattern: set methodName() {
        const setterMatch = currentLine.match(/set\s+(\w+)\s*\(/);
        if (setterMatch && i <= line) {
          return `set ${setterMatch[1]}()`;
        }

        // Look for method pattern: methodName() {
        const methodMatch = currentLine.match(/(\w+)\s*\(\s*.*\)\s*\{/);
        if (methodMatch && i <= line && !['if', 'for', 'while', 'switch', 'catch'].includes(methodMatch[1])) {
          return `${methodMatch[1]}()`;
        }
      }

      return null;
    } catch {
      return null;
    }
  }

  // REMOVED: Manual mappings don't scale - using intelligent detection instead

  // Configuración de seguridad para producción
  const PRODUCTION_CONFIG = {
    // Usar detección inteligente en lugar de mapeos manuales
    useIntelligentDetection: true,
    // Información adicional para debugging sin exponer código
    includeEnvironmentInfo: true,
    // NUNCA intentar source maps externos (evita 404s)
    fallbackToPrivateSourceMaps: false,
    // URL base deshabilitada para evitar requests innecesarios
    privateSourceMapUrl: null
  };

  // Intelligent method inference from context
  function inferGetterFromContext(frame, raw) {
    // Look for clues in the surrounding stack frames or file context
    if (frame.file && frame.file.includes('booking')) {
      // Common getter patterns in booking.js
      const getterPatterns = [
        'totals', 'totalsServices', 'totalsHotels', 'totalsByHotelByRoom', 'totalsPerPerson',
        'baseQuotes', 'paxTypes', 'bookingDays', 'services', 'hotels'
      ];

      // Check if any getter pattern appears in nearby context
      for (const pattern of getterPatterns) {
        if (raw.toLowerCase().includes(pattern.toLowerCase())) {
          return `get ${pattern}()`;
        }
      }
    }

    return null;
  }

  // Enhanced method detection from stack trace context
  function detectMethodFromStackTrace(frame) {
    if (!frame || !frame.raw) return null;

    try {
      const raw = frame.raw;

      // Priority 1: Extract from [as methodName] pattern - most reliable
      let match = raw.match(/at\s+(?:(\w+)\.)?(\w+)\s*\[as\s+(\w+)\]/);
      if (match) {
        const actualName = match[3]; // The actual getter/method name

        // Format as getter if it looks like one
        if (actualName && (actualName.includes('totals') || actualName.includes('Total') ||
          actualName.includes('get') || actualName.includes('set'))) {
          return `get ${actualName}()`;
        }

        return actualName ? `${actualName}()` : null;
      }

      // Priority 2: Standard function pattern
      match = raw.match(/at\s+(?:(\w+)\.)?(\w+)\s*\(/);
      if (match) {
        const className = match[1];
        const methodName = match[2];

        // If it's a generic 'get' method, try to infer the actual getter
        if (methodName === 'get' && className) {
          // Look for clues in the stack trace or file context
          const inferredMethod = inferGetterFromContext(frame, raw);
          if (inferredMethod) {
            return inferredMethod;
          }
          return `${className}.get()`;
        }

        return className ? `${className}.${methodName}()` : `${methodName}()`;
      }

      // Priority 3: Firefox pattern
      match = raw.match(/(\w+)@/);
      if (match) {
        return `${match[1]}()`;
      }

      return null;
    } catch {
      return null;
    }
  }

  // Analyze multiple frames to find the most relevant method
  function findMostRelevantMethod(frames) {
    if (!frames || frames.length === 0) return null;

    // Priority 1: Frame with [as methodName] in booking files
    for (const frame of frames) {
      if (frame.raw && frame.raw.includes('[as ') &&
        frame.file && frame.file.includes('booking')) {
        const method = detectMethodFromStackTrace(frame);
        if (method) return { method, frame };
      }
    }

    // Priority 2: Any frame with [as methodName]
    for (const frame of frames) {
      if (frame.raw && frame.raw.includes('[as ')) {
        const method = detectMethodFromStackTrace(frame);
        if (method) return { method, frame };
      }
    }

    // Priority 3: First booking-related frame
    for (const frame of frames) {
      if (frame.file && frame.file.includes('booking')) {
        const method = detectMethodFromStackTrace(frame);
        if (method) return { method, frame };
      }
    }

    // Priority 4: Any app frame (not node_modules)
    for (const frame of frames) {
      if (frame.file && !frame.file.includes('node_modules')) {
        const method = detectMethodFromStackTrace(frame);
        if (method) return { method, frame };
      }
    }

    return null;
  }

  // REMOVED: Complex context detection - using simpler intelligent detection instead

  function parseStackFrames(stackStr) {
    if (!stackStr) return [];
    const lines = String(stackStr).split('\n').map(l => l.trim()).filter(Boolean);
    const frames = [];

    for (const line of lines) {
      // Skip the error message line (first line that just contains the error type and message)
      if (line.match(/^(TypeError|ReferenceError|SyntaxError|Error):\s/) && !line.includes(' at ')) {
        // Don't include the error message line in frames - it's redundant
        continue;
      }

      // Chrome/Safari: at func (url:line:col) | at url:line:col
      let m = line.match(/at\s+(?:(.+?)\s+\((.+?):(\d+):(\d+)\)|(.+?):(\d+):(\d+))/);
      if (m) {
        const func = m[1] || null;
        const file = m[2] || m[5];
        const lineNum = Number(m[3] || m[6]);
        const colNum = Number(m[4] || m[7]);
        const cleanedFile = cleanWebpackPath(file);

        const frame = {
          function: func,
          file: cleanedFile,
          originalFile: file, // Keep original for debugging
          line: lineNum,
          column: colNum,
          raw: line,
          isSourceMapped: file && file.includes('webpack-internal:///')
        };

        // Enhance with intelligent method detection
        frame.detectedMethod = detectMethodFromStackTrace(frame);

        frames.push(frame);
        continue;
      }

      // Firefox: func@url:line:col
      m = line.match(/(.+?)@(.+?):(\d+):(\d+)/);
      if (m) {
        const func = m[1] || null;
        const file = m[2];
        const lineNum = Number(m[3]);
        const colNum = Number(m[4]);
        const cleanedFile = cleanWebpackPath(file);

        const frame = {
          function: func,
          file: cleanedFile,
          originalFile: file,
          line: lineNum,
          column: colNum,
          raw: line,
          isSourceMapped: file && file.includes('webpack-internal:///')
        };

        // Enhance with intelligent method detection
        frame.detectedMethod = detectMethodFromStackTrace(frame);

        frames.push(frame);
        continue;
      }

      // Skip unhelpful anonymous frames
      if (line.includes('at get (<anonymous>)') ||
        line.includes('(<anonymous>)') && !line.includes('webpack-internal')) {
        // Skip anonymous frames that don't provide useful information
        continue;
      }

      // Fallback for other formats (only if it might be useful)
      if (line.trim().length > 0) {
        frames.push({ function: null, file: null, line: null, column: null, raw: line });
      }
    }

    // Clean up and optimize frames
    return cleanupFrames(frames);
  }

  function cleanupFrames(frames) {
    return frames.filter(frame => {
      // Remove frames with no useful information
      if (!frame.file && !frame.function && frame.raw) {
        // Keep only if it contains useful debugging info
        const raw = frame.raw.toLowerCase();
        if (raw.includes('error:') || raw.includes('exception')) {
          return false; // Skip error message lines
        }
        if (raw.includes('anonymous') && !raw.includes('webpack')) {
          return false; // Skip unhelpful anonymous frames
        }
      }

      // Keep frames that have location info or useful function names
      return frame.file || frame.function ||
        (frame.raw && frame.raw.includes('webpack-internal'));
    }).map((frame, index) => {
      // Add frame index for debugging
      return { ...frame, frameIndex: index };
    });
  }

  function parseLocation(error, evt) {
    if (evt && evt.target && (evt.target.src || evt.target.href)) {
      const fileUrl = evt.target.src || evt.target.href;
      return { file: cleanWebpackPath(fileUrl), line: null, column: null, frames: [] };
    }
    if (evt && (evt.filename || evt.lineno || evt.colno)) {
      return {
        file: cleanWebpackPath(evt.filename) || null,
        line: Number(evt.lineno) || null,
        column: Number(evt.colno) || null,
        frames: [],
      };
    }
    const frames = parseStackFrames(error?.stack);
    const top = frames.find(f => f.file) || { file: null, line: null, column: null };
    return { ...top, frames };
  }

  function getBrowserAndDevice() {
    const nav = (typeof navigator !== 'undefined') ? navigator : null;
    const ua = nav?.userAgent || '';
    const uaData = nav?.userAgentData || null;
    const platform = nav?.platform || null;

    let browser = null, browserVersion = null;
    if (uaData?.brands?.length) {
      const preferred = uaData.brands.find(b => !/Chromium|Not\.A|Brand/i.test(b.brand)) || uaData.brands[0];
      if (preferred) {
        const parts = String(preferred.brand).split('/');
        browser = parts[0] || preferred.brand;
        browserVersion = preferred.version || null;
      }
    }
    if (!browser) {
      const m =
        ua.match(/Edg\/(\d+\.\d+\.\d+\.\d+|[\d.]+)/) ||
        ua.match(/OPR\/(#[\d.]+)/) ||
        ua.match(/OPR\/(\d+[\d.]*)/) ||
        ua.match(/Firefox\/(\d+[\d.]*)/) ||
        ua.match(/Chrome\/(\d+[\d.]*)/) ||
        ua.match(/Version\/(\d+[\d.]+).*Safari/);
      if (m) {
        if (/Edg\//.test(ua)) browser = 'Microsoft Edge';
        else if (/OPR\//.test(ua) || /Opera/.test(ua)) browser = 'Opera';
        else if (/Firefox\//.test(ua)) browser = 'Firefox';
        else if (/Chrome\//.test(ua)) browser = 'Chrome';
        else browser = 'Safari';
        browserVersion = m[1];
      } else {
        browser = ua ? 'Unknown' : 'Unavailable';
      }
    }

    let device = 'desktop';
    if (uaData && typeof uaData.mobile === 'boolean') {
      device = uaData.mobile ? 'mobile' : 'desktop';
    } else if (/\b(iPad|Tablet|Nexus 7|Nexus 9|SM-T|Kindle|Silk|PlayBook)\b/i.test(ua)) {
      device = 'tablet';
    } else if (/Mobi|Android.*Mobile|iPhone|Phone/i.test(ua)) {
      device = 'mobile';
    }

    return { browser, browserVersion, device, platform, ua: ua || null };
  }

  function getRouteInfo() {
    try {
      const r = window?.appRouter?.currentRoute; // expose your router as window.appRouter
      if (!r) return { path: location.hash || location.pathname, name: null, params: null, query: null };
      const route = r.value || r; // vue-router 4 uses refs
      return {
        path: route.fullPath || route.path,
        name: route.name || null,
        params: route.params || null,
        query: route.query || null
      };
    } catch {
      return { path: location.href, name: null, params: null, query: null };
    }
  }

  const sessionId = (function () {
    try {
      const k = 'app_session_id';
      let v = localStorage.getItem(k);
      if (!v) { v = (crypto.randomUUID && crypto.randomUUID()) || String(Math.random()); localStorage.setItem(k, v); }
      return v;
    } catch { return null; }
  })();

  // --- 2) Console buffer ----------------------------------------------------
  const logBuffer = [];
  
  const levelsToCapture = CONFIG.captureWarnings 
    ? ['log', 'warn', 'error', 'info']
    : ['error'];
    
  levelsToCapture.forEach(type => {
    const original = console[type];
    console[type] = function (...args) {
      try {
        const line = `[${new Date().toISOString()}] [${type.toUpperCase()}] ${args.map(a => {
          if (typeof a === 'string') return a;
          try { return safeSerialize(a); } catch { return '[Unserializable]'; }
        }).join(' ')}`;
        logBuffer.push(line);
        if (logBuffer.length > CONFIG.maxLogBuffer) logBuffer.shift();
      } catch { }
      try { original.apply(console, args); } catch { }
    };
  });

  // --- 3) Dedupe dictionary -------------------------------------------------
  const recentErrors = Object.create(null); // key -> { t: timestamp, count: number }

  function getErrorKey(obj) {
    const stack = obj?.stack || '';
    const message = obj?.message || String(obj);
    return (message + stack).slice(0, CONFIG.maxKeyLen);
  }

  // --- 4) Sender ------------------------------------------------------------
  function sendPayload(body) {
    // Prefer project-specific api client if present
    try {
      if (window.api && typeof window.api.post === 'function') {
        return window.api.post(CONFIG.endpoint, JSON.stringify(body));
      }
    } catch { }

    // Fallback to fetch
    try {
      return fetch(CONFIG.endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
        credentials: 'include',
        keepalive: true, // allow during unload
      }).catch(() => { });
    } catch { }
  }

  // --- 5) Reporter ----------------------------------------------------------
  async function reportErrorOnce(errorObj, evt) {
    try {
      if (shouldIgnoreError(errorObj, evt)) {
        return;
      }
      
      const norm = normalizeError(errorObj);
      const key = getErrorKey({ message: norm.message, stack: norm.stack });
      const now = Date.now();

      const rec = recentErrors[key];
      if (!rec || now - rec.t > CONFIG.throttleMs) {
        recentErrors[key] = { t: now, count: 1 };
      } else {
        rec.count++;
        if (rec.count > 5) return; // drop noisy duplicates within window
      }

      const loc = parseLocation(norm, evt);
      const { browser, browserVersion, device, platform, ua } = getBrowserAndDevice();
      const routeInfo = getRouteInfo();

      // Enhance frames with source map resolution (DISABLED for production)
      const enhancedFrames = [];
      // Always use basic frames without source map resolution to avoid 404 requests
      enhancedFrames.push(...(loc.frames || []).slice(0, CONFIG.maxFrames));

      // Use intelligent method detection to find the most relevant frame
      const relevantMethod = findMostRelevantMethod(enhancedFrames);
      const primaryFrame = relevantMethod ? relevantMethod.frame :
        (enhancedFrames.find(f => f.file && f.line) || loc);

      const payload = {
        url: location.href,
        route: routeInfo,
        error: {
          name: norm.name,
          message: norm.message,
          stack: norm.stack ? norm.stack.split('\n') : null,
          file: primaryFrame.resolvedSource || primaryFrame.file || loc.file,
          line: primaryFrame.correctedLine || primaryFrame.resolvedLine || primaryFrame.line || loc.line,
          column: primaryFrame.resolvedColumn || primaryFrame.column || loc.column,
          method: relevantMethod ? relevantMethod.method : (primaryFrame.detectedMethod || primaryFrame.function || null),
          frames: enhancedFrames.map(frame => ({
            ...frame,
            // Add source map indicators for better debugging
            hasSourceMap: frame.isSourceMapped,
            cleanedPath: frame.file !== frame.originalFile ? frame.file : null,
            // Enhanced source map resolution
            resolvedLocation: frame.hasResolvedSourceMap ? {
              source: frame.resolvedSource,
              line: frame.resolvedLine,
              column: frame.resolvedColumn,
              method: frame.extractedMethod
            } : null
          })),
          // Enhanced source map info
          sourceMapInfo: {
            primaryFile: primaryFrame.resolvedSource || primaryFrame.file || loc.file,
            primaryLine: primaryFrame.correctedLine || primaryFrame.resolvedLine || primaryFrame.line || loc.line,
            primaryColumn: primaryFrame.resolvedColumn || primaryFrame.column || loc.column,
            primaryMethod: relevantMethod ? relevantMethod.method : (primaryFrame.detectedMethod || primaryFrame.function || null),
            hasIntelligentDetection: !!relevantMethod,
            hasSourceMaps: loc.frames && loc.frames.some(f => f.isSourceMapped),
            hasResolvedSourceMaps: enhancedFrames.some(f => f.hasResolvedSourceMap),
            sourceMappedFrames: loc.frames ? loc.frames.filter(f => f.isSourceMapped).length : 0,
            resolvedFrames: enhancedFrames.filter(f => f.hasResolvedSourceMap).length,
            totalFrames: loc.frames ? loc.frames.length : 0
          }
        },
        user: (window.api && window.api.currentUser && window.api.currentUser.UserName) || null,
        sessionId,
        logs: logBuffer.slice(-50),
        timestamp: new Date().toISOString(),
        browser, browserVersion, device, platform, ua,
        referrer: document.referrer || null,
        visibility: document.visibilityState || null,
        perfNow: (typeof performance !== 'undefined') ? performance.now() : null,
        appVersion: window.__APP_VERSION__ || null,
        // Build info for source map correlation
        buildInfo: {
          sourceMapEnabled: true,
          environment: process.env.NODE_ENV || 'unknown',
          webpackBuild: true
        }
      };

      sendPayload(payload);
    } catch (e) {
      // Swallow any reporting errors to avoid breaking the app
      try { console.warn('[error-reporter] failed to report error:', e); } catch { }
    }
  }

  // Expose for manual reporting if needed
  window.reportErrorOnce = reportErrorOnce;

  // --- 6) Global listeners --------------------------------------------------
  window.addEventListener('unhandledrejection', function (event) {
    const reason = event && event.reason;
    if (reason instanceof Error) {
      reportErrorOnce(reason);
    } else {
      reportErrorOnce(new Error(String(reason)));
    }
  });

  window.addEventListener('error', function (event) {
    if (event && event.error instanceof Error) {
      reportErrorOnce(event.error, event);
    } else {
      const syntheticError = new Error(event?.message || 'Unknown error');
      const resUrl = (event && event.target && (event.target.src || event.target.href))
        ? (event.target.src || event.target.href)
        : (event && event.filename) || null;
      const ln = (event && event.lineno != null) ? event.lineno : null;
      const col = (event && event.colno != null) ? event.colno : null;
      if (resUrl) {
        syntheticError.stack = `at ${resUrl}${ln != null && col != null ? `:${ln}:${col}` : ''}`;
      }
      reportErrorOnce(syntheticError, event);
    }
  });

  // --- 7) Vue hooks (optional) ---------------------------------------------
  // Call these from your app bootstrap if using Vue
  window.installVue2ErrorHandler = function (Vue) {
    if (!Vue || !Vue.config) return;
    Vue.config.errorHandler = (err, vm, info) => {
      try { reportErrorOnce(err); } catch { }
      try { console.error('Vue2 error:', err, info); } catch { }
    };
  };

  window.installVue3ErrorHandler = function (app) {
    if (!app || !app.config) return;
    app.config.errorHandler = (err, instance, info) => {
      try { reportErrorOnce(err); } catch { }
      try { console.error('Vue3 error:', err, info); } catch { }
    };
  };

  // --- 8) Helpful notes -----------------------------------------------------
  //  - Expose your router as window.appRouter to enrich route info.
  //  - Set window.__APP_VERSION__ at build time (e.g., commit SHA) for tracing.
  //  - For source map server-side mapping, upload .map files and map stacks on backend.
  //  - For CORSed scripts, use crossorigin="anonymous" and proper ACAO headers.
})();
